import {
    Accordion,
    AccordionItem,
    AccordionPanel,
    Flex,
    Text,
    Box,
    ThemeColorVariable,
    ThemeFontSizeVariable,
    ThemeSpaceVariable,
    Heading,
    Divider,
} from "@caspeco/casper-ui-library";
import { useAvailableTimes } from "api/api-hooks/use-available-times";
import { useWebBooking } from "api/api-hooks/use-web-booking";
import { useBookingStateContext } from "hooks/use-booking-state";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { ChargeType } from "types/charge-type";
import { hasAvailableWaitListOffers, isWaitList } from "utils/booking";
import { getRuleById } from "utils/web-rule-utils";
import BookingMessage from "./booking-message";
import CardRegistrationInformation from "./card-registration-information";
import PrepaymentInformation from "./prepayment-information";
import { BookingSummaryItem } from "components/my-booking/booking-summary/booking-summary-list-item";
import Summary from "components/selection-components/summary/summary";
import ResturantInformation from "components/my-booking/resturant-information";

interface BookingSummaryProps {
    defaultOpen?: boolean;
}

export const BookingSummary = ({ defaultOpen = false }: BookingSummaryProps) => {
    const { t } = useTranslation();
    const { data: booking } = useWebBooking();
    const { date, times } = useBookingStateContext();
    const availableTimes = useAvailableTimes(date);
    const accordionRef = useRef<HTMLDivElement>(null);

    const handleAccordionChange = (index: number | number[]) => {
        const isOpen = Array.isArray(index) ? index.length > 0 : index >= 0;
        if (isOpen) {
            // Wait for the panel expand animation to finish so the page is tall
            // enough to actually scroll the revealed content into view.
            setTimeout(() => {
                accordionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 0);
        }
    };

    if (!booking) {
        return null;
    }

    const hasAvailableOffers = hasAvailableWaitListOffers(booking);
    const isWaitListBooking = isWaitList(booking);

    const contactName = `${booking.contact.fname} ${booking.contact.lname}`;
    const startTimes = booking.articles
        .filter((art) => art.isTable)
        .sort((a, b) => a.start.valueOf() - b.start.valueOf())
        .map((time) => time.start.format("HH:mm"))
        .join(", ");

    const showWaitListSummary = isWaitListBooking && !hasAvailableOffers;

    const ruleById = getRuleById(times ? (times[0]?.ruleId ?? -1) : -1, availableTimes.data || []);

    const ruleName = ruleById
        ? ruleById.title.toLowerCase()
        : booking.webBookingRules?.at(0)?.title.toLowerCase();

    return (
        <Box ref={accordionRef}>
            <Accordion defaultIndex={defaultOpen ? 0 : undefined} onChange={handleAccordionChange}>
                <AccordionItem title={t("bookingDetails")}>
                    {/* @ts-expect-error design requires no top padding, and pt is not allowed on AccordionPanel in the types, but works because the props are spread to the underlying panel in casper-ui. It should be added to the interface for the component. */}
                    <AccordionPanel pt="0">
                        {!showWaitListSummary && <Summary />}
                        <Divider mb="16px" />
                        <Flex
                            direction="column"
                            bg={ThemeColorVariable.Surface}
                            gap={ThemeSpaceVariable.Medium}
                        >
                            {showWaitListSummary && (
                                <Box>
                                    <Text fontWeight="medium" mb={ThemeSpaceVariable.Small}>
                                        {t("waitList.waitListFor", {
                                            webTimeRuleName: ruleName,
                                        })}
                                    </Text>
                                    <BookingSummaryItem
                                        label={
                                            booking.articles.length === 1
                                                ? t("startTime")
                                                : t("startTimes")
                                        }
                                        value={startTimes}
                                    />
                                </Box>
                            )}
                            {!!booking.message && <BookingMessage message={booking.message} />}
                            {booking.chargeType === ChargeType.NoShow && (
                                <>
                                    <CardRegistrationInformation />
                                    <Divider />
                                </>
                            )}
                            {booking.chargeType === ChargeType.PrePayment && (
                                <>
                                    <PrepaymentInformation />
                                    <Divider />
                                </>
                            )}
                            <Box>
                                <Heading
                                    as="h4"
                                    fontWeight="500"
                                    fontSize={ThemeFontSizeVariable.Large}
                                    pb={ThemeSpaceVariable.Small}
                                >
                                    {t("myBooking.mycontactinfo")}
                                </Heading>
                                {booking.customer.name && <Text>{booking.customer.name}</Text>}
                                <Text>{contactName}</Text>
                                <Text>{booking.contact.email}</Text>
                                {booking.contact.mobile && <Text>{booking.contact.mobile}</Text>}
                            </Box>
                            <ResturantInformation />
                        </Flex>
                    </AccordionPanel>
                </AccordionItem>
            </Accordion>
        </Box>
    );
};
